import { Ban, Check, CircleAlert, Copy, Landmark } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import type { CuentaRestringida, DatosTransferencia } from '@/api/errors';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { fechaLarga, mxn } from '@/lib/formato';
import { cn } from '@/lib/utils';

/**
 * Modal del login cuando la API responde 403 `ACCOUNT_SUSPENDED` o `ACCOUNT_BLOCKED` (al iniciar
 * sesión, en la verificación 2FA o al renovar la sesión). Suspendida: motivo, adeudo, fecha límite
 * y datos de transferencia; bloqueada: el mensaje de la API y el motivo.
 */
export function DialogoCuentaRestringida({
  restriccion,
  onClose,
}: {
  restriccion: CuentaRestringida | null;
  onClose: () => void;
}) {
  const entendido = useRef<HTMLButtonElement>(null);
  return (
    <Dialog open={restriccion !== null} onOpenChange={(abierto) => !abierto && onClose()}>
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto p-5 sm:p-6"
        onOpenAutoFocus={(e) => {
          // El foco va al botón principal, no al de copiar la CLABE (el primero de la lista).
          e.preventDefault();
          entendido.current?.focus();
        }}
      >
        {restriccion?.code === 'ACCOUNT_SUSPENDED' && <Suspendida r={restriccion} />}
        {restriccion?.code === 'ACCOUNT_BLOCKED' && <Bloqueada r={restriccion} />}
        <div className="mt-6 flex justify-end">
          <DialogClose asChild>
            <button ref={entendido} type="button" className="btn-cs btn-primario w-full sm:w-auto">
              Entendido
            </button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}

type Suspension = Extract<CuentaRestringida, { code: 'ACCOUNT_SUSPENDED' }>;
type Bloqueo = Extract<CuentaRestringida, { code: 'ACCOUNT_BLOCKED' }>;

function Encabezado({ tono, children }: { tono: 'aviso' | 'peligro'; children: ReactNode }) {
  const Icono = tono === 'aviso' ? CircleAlert : Ban;
  return (
    <div className="flex items-start gap-3">
      <span
        className={cn(
          'grid size-11 shrink-0 place-items-center rounded-full bg-[var(--vidrio-fuerte)]',
          tono === 'aviso' ? 'text-amarillo-texto' : 'text-rojo-texto',
        )}
        aria-hidden
      >
        <Icono size={24} />
      </span>
      <div className="min-w-0 pt-1">{children}</div>
    </div>
  );
}

function Motivo({ texto }: { texto: string | null }) {
  if (!texto) return null;
  return (
    <div className="mt-4 rounded-2xl bg-[var(--vidrio-fuerte)] px-4 py-3">
      <p className="text-xs font-bold tracking-wide text-texto-3 uppercase">Motivo</p>
      <p className="mt-1 text-sm break-words whitespace-pre-line text-texto">{texto}</p>
    </div>
  );
}

function Suspendida({ r }: { r: Suspension }) {
  return (
    <>
      <Encabezado tono="aviso">
        <DialogTitle>{r.message.replace(/\.$/, '') || 'Tu cuenta está suspendida'}</DialogTitle>
        <DialogDescription>
          {r.amountDue
            ? 'Para reactivarla, cubre el adeudo y envíanos tu comprobante de pago.'
            : 'Mientras la suspensión siga vigente no podrás iniciar sesión.'}
        </DialogDescription>
      </Encabezado>
      <Motivo texto={r.reason} />
      {(r.amountDue || r.dueOn) && (
        <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {r.amountDue && (
            <Dato etiqueta="Adeudo">
              <span className="text-lg font-extrabold text-cian-texto tabular-nums">
                {mxn(r.amountDue)}
              </span>
            </Dato>
          )}
          {r.dueOn && (
            <Dato etiqueta="Fecha límite de pago">
              <span className="font-bold">{fechaLarga(r.dueOn)}</span>
            </Dato>
          )}
        </dl>
      )}
      {r.payment && <Transferencia p={r.payment} />}
    </>
  );
}

function Bloqueada({ r }: { r: Bloqueo }) {
  return (
    <>
      <Encabezado tono="peligro">
        <DialogTitle>Tu cuenta está bloqueada</DialogTitle>
        <DialogDescription>{r.message}</DialogDescription>
      </Encabezado>
      <Motivo texto={r.reason} />
    </>
  );
}

function Dato({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-bold text-texto-3">{etiqueta}</dt>
      <dd className="mt-0.5 break-words text-texto">{children}</dd>
    </div>
  );
}

/** "012 180 00123456789 0": banco, plaza, cuenta y dígito verificador (solo para leerla). */
function clabeLegible(clabe: string): string {
  const d = clabe.replace(/\s+/g, '');
  return /^\d{18}$/.test(d)
    ? `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 17)} ${d.slice(17)}`
    : clabe;
}

function Transferencia({ p }: { p: DatosTransferencia }) {
  return (
    <section
      aria-labelledby="titulo-transferencia"
      className="mt-5 rounded-2xl border border-[var(--separador)] p-4"
    >
      <h3
        id="titulo-transferencia"
        className="flex items-center gap-2 text-sm font-extrabold text-azul-texto"
      >
        <Landmark size={18} aria-hidden /> Datos para transferencia
      </h3>
      <dl className="mt-3 space-y-3">
        {p.bank && <Dato etiqueta="Banco">{p.bank}</Dato>}
        <div>
          <dt className="text-xs font-bold text-texto-3">CLABE</dt>
          <dd className="mt-0.5 flex flex-wrap items-center gap-2">
            <span className="font-mono text-base font-bold tracking-wide text-texto tabular-nums">
              {clabeLegible(p.clabe)}
            </span>
            <BotonCopiar valor={p.clabe.replace(/\s+/g, '')} />
          </dd>
        </div>
        {p.holder && <Dato etiqueta="Titular">{p.holder}</Dato>}
      </dl>
      {p.email && (
        <p className="mt-4 text-sm text-texto-2">
          Envía tu comprobante a{' '}
          <a href={`mailto:${p.email}`} className="font-bold break-all">
            {p.email}
          </a>
          .
        </p>
      )}
    </section>
  );
}

function BotonCopiar({ valor }: { valor: string }) {
  const [estado, setEstado] = useState<'listo' | 'copiada' | 'error'>('listo');
  useEffect(() => {
    if (estado === 'listo') return;
    const id = window.setTimeout(() => setEstado('listo'), 2500);
    return () => window.clearTimeout(id);
  }, [estado]);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(valor);
      setEstado('copiada');
    } catch {
      setEstado('error');
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => void copiar()}
        className="btn-cs btn-vidrio px-3 py-1.5 text-sm"
        aria-label="Copiar CLABE"
      >
        {estado === 'copiada' ? (
          <Check size={16} className="text-verde-texto" aria-hidden />
        ) : (
          <Copy size={16} aria-hidden />
        )}
        {estado === 'copiada' ? 'Copiada' : 'Copiar'}
      </button>
      <span role="status" aria-live="polite" className="sr-only">
        {estado === 'copiada' && 'CLABE copiada al portapapeles.'}
        {estado === 'error' && 'No pudimos copiar la CLABE.'}
      </span>
      {estado === 'error' && (
        <span className="basis-full text-xs font-semibold text-rojo-texto" aria-hidden>
          No pudimos copiarla; selecciónala y cópiala a mano.
        </span>
      )}
    </>
  );
}
